import { CustomFieldDefinition, InventoryAsset } from '../../../types';
import { recordCustomFieldEditable } from '../../../utils/customFieldEditability';
import { CustomFieldCapability } from '../../../utils/customFieldCapabilities';
import { ReactNode } from 'react';
import { getNestedValue } from '../../../utils/helpers';
import { DetailViewCard } from '../../Compounds/CardWithTitle';
import { DetailRow } from '../../Compounds/DetailsDrawer/DetailRow';
import { CustomFieldDetailRow } from '../../Compounds/DetailsDrawer/CustomFieldDetailRow';
import { CaseDetailsDisplay } from '../SupportCases/CaseDetailsDisplay';
import { PermissibleRender } from '../../Compounds/PermissibleRender';
import { Permission } from '../../../store/GeneralStore';
import { useEffect, useState } from 'react';
import { getInventoryLoadingSubject, getInventorySubject } from '../../../store/Inventory';
import { AttachmentTable } from '../../Compounds/AttachmentTable';
import { fetchNotificationsByEntity, getNotificationsSubject, Notification } from '../../../store/Notifications';
import { InstallNotifications } from './InstallNotifications';
import { defaultFeatureFlags, FeatureFlags, getFeatureFlagSubject } from '../../../Api/useFeatureFlags';
import { ComponentSpinner } from '../../Compounds/Loading/ComponentSpinner';
import {
  DefaultInventoryGridColumnDefs,
  SupportNumberDefaults,
  inventoryRowAccountId
} from './inventoryGridColumnDefs';
import { TAGS_FIELD } from '../../Compounds/UpstackDataGrid/tagColumns';
import { DetailsComments } from '../../Compounds/DetailsComments/DetailsComments';
import {
  getUser,
  getUserAccountById,
  isCapabilityDenied,
  isInternalUser,
  shouldSeeComments,
  shouldSeeSupplierContacts,
  getEffectiveAccountId
} from '../../../store/User';
import { useAttachments } from '../../../store/Attachment';
import { VoiceDetails } from '../../Compounds/VoiceDetails/VoiceDetails';
import { formatContractEndDate } from './helpers';
import { useRecordCustomFieldDefs } from '../../../Api/Admin/CustomFields/useRecordCustomFieldDefs';
import { upsertCustomFieldValuesApi } from '../../../Api/Admin/CustomFields/customFieldValuesApi';
import { useSnackbar } from '../../../Context/SnackbarContext';
import { extractApiErrorMessage } from '../../Compounds/McpAccess/extractApiErrorMessage';
import { FAILED_SAVE } from '../../Compounds/UpstackDataGrid/customFieldColumns';
import { productLabel } from '../../../utils/productLabel';

export interface UpstackInventoryDetailsProps {
  data: InventoryAsset;
  extraSection?: ReactNode;
}

export function UpstackInventoryDetails({ data, extraSection }: UpstackInventoryDetailsProps) {
  const { setSnack } = useSnackbar();
  const [inventoryLoading, setInventoryLoading] = useState<boolean>(false);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [featureFlags, setFeatureFlags] = useState<FeatureFlags>(defaultFeatureFlags);
  const [page, setPage] = useState<number>(1);
  const [fieldList, setFieldList] = useState<{ colId: string; title: string }[]>([]);
  const { fetch } = useAttachments();
  const customFieldDefs = useRecordCustomFieldDefs(
    inventoryRowAccountId(data) || getEffectiveAccountId(),
    data.custom_fields
  );
  const customFieldUnrestricted = isInternalUser(getUser());
  const customFieldDenied = isCapabilityDenied(CustomFieldCapability.VALUES_UPSERT, getUser());
  const customFieldLocked = (def: CustomFieldDefinition) =>
    !recordCustomFieldEditable(data.custom_fields, def, customFieldUnrestricted, customFieldDenied);

  useEffect(() => {
    const flags = getFeatureFlagSubject().subscribe((flags) => setFeatureFlags(flags));
    const inventoryLoadingSub = getInventoryLoadingSubject().subscribe((loading: boolean) => {
      setInventoryLoading(loading);
    });

    return () => {
      if (flags) flags.unsubscribe();
      if (inventoryLoadingSub) inventoryLoadingSub.unsubscribe();
    };
  }, []);

  const handleCustomFieldSave = async (definitionId: string, value: string) => {
    if (!data.id) return;
    const { error } = await upsertCustomFieldValuesApi(data.id, 'Inventory', [
      { custom_field_definition_id: definitionId, value }
    ]);
    if (error) {
      setSnack({ message: extractApiErrorMessage(error, FAILED_SAVE), open: true, type: 'error' });
      throw new Error(error.message);
    }
    const def = customFieldDefs.find((d) => d.id === definitionId);
    const updatedAsset: InventoryAsset = {
      ...data,
      custom_fields: (data.custom_fields ?? []).some((cf) => cf.definition_id === definitionId)
        ? (data.custom_fields ?? []).map((cf) => (cf.definition_id === definitionId ? { ...cf, value } : cf))
        : [
            ...(data.custom_fields ?? []),
            {
              definition_id: definitionId,
              name: def?.name ?? '',
              field_type: def?.field_type ?? 'text',
              position: def?.position ?? 0,
              value,
              is_editable: def?.is_editable,
              inherited: false
            }
          ]
    };
    const current = getInventorySubject().getValue();
    getInventorySubject().next(current.map((a) => (a.id === data.id ? updatedAsset : a)));
  };

  const getValue = (key: string) => {
    if (JSON.stringify(data) === '{}') return ''; // TODO: temporary fix
    if (key === 'contractEndDate') return formatContractEndDate(data?.contractEndDate, data?.term);
    if (key === 'product') return productLabel(data?.product, { abbreviated: false });
    if (key === 'origin') return 'Supported';
    if (key.includes('.')) return getNestedValue(data, key);
    if (key === 'account_id' && data?.account_id)
      return data?.accountName || getUserAccountById(data?.account_id)?.name;
    if (key.startsWith('custom_field_')) {
      const defId = key.slice('custom_field_'.length);
      return data.custom_fields?.find((cf) => cf.definition_id === defId)?.value ?? '';
    }

    const val = data[key as keyof InventoryAsset] as string;
    return val || '';
  };

  useEffect(() => {
    const columns = [...DefaultInventoryGridColumnDefs];
    if (shouldSeeSupplierContacts()) {
      columns.push(...SupportNumberDefaults);
    }
    const baseFields = columns
      .map((item) => {
        return {
          colId: item?.field,
          title: item?.field === 'origin' ? 'Support Level' : item?.headerName || ''
        };
      })
      // A detail row renders its value as text, and the tags array is truthy: it would junk-row or crash it.
      .filter((field) => !['id', 'detailCTA', 'sfId', TAGS_FIELD].includes(field.colId) && getValue(field.colId) !== '')
      .sort((a, b) => {
        return a.colId === 'origin' ? -1 : b.colId === 'origin' ? 1 : 0;
      });

    const customFields =
      customFieldDefs.length > 0
        ? customFieldDefs.map((def) => ({ colId: `custom_field_${def.id}`, title: def.name }))
        : (data.custom_fields ?? [])
            // Related Inventory values belong to the project pages and the grid; this drawer lists the record's own.
            .filter((cf) => (cf.entity_type ?? 'Inventory') === 'Inventory' && cf.value != null && cf.value !== '')
            .sort((a, b) => a.position - b.position)
            .map((cf) => ({ colId: `custom_field_${cf.definition_id}`, title: cf.name }));

    const productIndex = baseFields.findIndex((f) => f.colId === 'productDisplay');
    const insertAt = productIndex >= 0 ? productIndex + 1 : baseFields.length;

    setFieldList([...baseFields.slice(0, insertAt), ...customFields, ...baseFields.slice(insertAt)]);
  }, [featureFlags, data.custom_fields, customFieldDefs]);

  const filterAndSortNotifications = (allNotifications: Notification[], entityId: string): Notification[] => {
    if (!entityId) return [];

    const filtered = allNotifications.filter((n) => n.entity_id === entityId && !n.is_archived);
    return filtered.sort((a, b) => Number(a.is_read) - Number(b.is_read));
  };

  useEffect(() => {
    if (!data.id) return;

    const notificationsSub = getNotificationsSubject().subscribe((allNotifications) => {
      const updatedNotifications = filterAndSortNotifications(allNotifications, data.id as string);
      setNotifications(updatedNotifications);
    });

    return () => notificationsSub.unsubscribe();
  }, [data.id]);

  useEffect(() => {
    const loadNotifications = async () => {
      if (data.id) {
        const fetchedNotifications = await fetchNotificationsByEntity(data.id, page, notifications);
        if (fetchedNotifications) setNotifications(fetchedNotifications);
      }
    };
    loadNotifications();
  }, [data.id, page]);

  const fetchMore = () => {
    setPage((prevPage) => prevPage + 1);
  };

  const fetchAttachments = () => {
    if (data?.id) {
      fetch(data?.id);
    }
  };

  return (
    <>
      <DetailViewCard
        collapsed={false}
        enableCollapse
        key="inventory"
        title="Details">
        <div className="overflow-auto h-[30vh]">
          <>
            {inventoryLoading && (
              <div className="mt-32">
                <ComponentSpinner />
              </div>
            )}
            {!inventoryLoading &&
              fieldList.map((field) => {
                if (field.colId.startsWith('custom_field_')) {
                  const defId = field.colId.slice('custom_field_'.length);
                  const def = customFieldDefs.find((d) => d.id === defId);
                  if (def) {
                    return (
                      <CustomFieldDetailRow
                        key={field.colId}
                        definition={def}
                        value={getValue(field.colId) || ''}
                        onSave={handleCustomFieldSave}
                        readOnly={customFieldLocked(def)}
                      />
                    );
                  }
                }
                return (
                  <DetailRow
                    key={field.colId}
                    colId={field.colId}
                    title={field.title}
                    value={getValue(field.colId) || ''}
                  />
                );
              })}
          </>
        </div>
      </DetailViewCard>
      {extraSection}
      <VoiceDetails data={data} />
      <PermissibleRender requiredPermissions={[Permission.VIEW_CASES_ON_INVENTORY]}>
        <CaseDetailsDisplay
          data={data}
          onCasesChanged={async () => fetchAttachments()}
        />
      </PermissibleRender>
      {data.id && (
        <AttachmentTable
          id={data.id}
          parentType="Asset"
        />
      )}
      {shouldSeeComments() && data?.id && (
        <DetailsComments
          id={data.id}
          entityType="Inventory"
          entityAccountId={data.account_id || ''}
        />
      )}
      {!!notifications.length && (
        <InstallNotifications
          filteredNotifications={notifications}
          fetchMore={fetchMore}
        />
      )}
    </>
  );
}
